import { useMemo, useCallback } from "react";

import { isOrganizationMember, isWorkspaceInvite, NormalizedMemberData } from "./utils";

import { useOrganizationRoles } from "@/queries/organizations";
import { usePatchOrgMember, useDeleteOrgMember } from "@/queries/organization-members";
import { useDeleteWorkspaceInvite } from "@/queries/workspace";
import { useIsOrgOwnerById } from "@/hooks/organizations";
import { useConfirm } from "@/context/confirm-context";
import { Action } from "@/components/ui/DropdownMenu";
import { ContextMenuItems } from "@/components/ui/ContextMenu";
import { OrganizationMember } from "@/types/organization-member";
import type { OrganizationRole } from "@/types/organization-roles";
import { WorkspaceInvite } from "@/types/workspace-invite";
import { ConfirmationModalVariant } from "@/components/ConfirmationModal";
import { ToastType } from "@/enums";
import { displayToast } from "@/utils/toast";

export const useOrganizationMembersActions = (
  organizationId?: number,
  onMemberDelete?: () => void,
  onInviteDelete?: () => void,
  onMemberRolePatched?: (newRole: OrganizationRole) => void,
) => {
  const isOrgOwner = useIsOrgOwnerById(organizationId ?? 0);
  const { data: organizationRoles } = useOrganizationRoles();
  const patchOrgMemberRole = usePatchOrgMember();
  const deleteOrganizationMember = useDeleteOrgMember();
  const deleteWorkspaceInviteSilent = useDeleteWorkspaceInvite({ silent: true });
  const { confirm } = useConfirm();

  const handleRoleChange = useCallback(
    async (member: NormalizedMemberData, roleId: string) => {
      if (!isOrganizationMember(member)) {
        return;
      }

      const selectedRoleId = parseInt(roleId, 10);
      if (member.organization_role.id === selectedRoleId) {
        return;
      }

      const isConfirmed = await confirm({
        title: `Update role of ${member.user.email}`,
        message: `Are you sure you want to update the role of this member?`,
        headerVariant: ConfirmationModalVariant.warning,
        saveButtonVariant: "default",
        exitButtonVariant: "outline",
      });

      if (isConfirmed) {
        try {
          const response = await patchOrgMemberRole.mutateAsync({
            organizationMemberId: member.organization_member_id,
            organizationRoleId: selectedRoleId,
          });
          if (response.ok) {
            const newRole = organizationRoles?.find((r: { id: number }) => r.id === selectedRoleId);
            if (newRole) {
              onMemberRolePatched?.(newRole);
            }
            onMemberDelete?.();
          }
        } catch {
          // Error handled by mutation
        }
      }
    },
    [confirm, patchOrgMemberRole, organizationRoles, onMemberRolePatched, onMemberDelete],
  );

  const handleDeleteMember = useCallback(
    async (member: NormalizedMemberData) => {
      if (isWorkspaceInvite(member)) {
        try {
          await Promise.all(
            member.invites.map(invite => deleteWorkspaceInviteSilent.mutateAsync(invite.id)),
          );
          onInviteDelete?.();
        } catch {
          // Error handled by mutation
        }
        displayToast(
          member.invites.length > 1
            ? "Invites cancelled successfully"
            : "Invite cancelled successfully",
          ToastType.success,
        );
      } else {
        try {
          await deleteOrganizationMember.mutateAsync(member.organization_member_id);
          onMemberDelete?.();
        } catch {
          // Error handled by mutation
        }
      }
    },
    [deleteWorkspaceInviteSilent, deleteOrganizationMember, onInviteDelete, onMemberDelete],
  );

  const handleLeaveOrganization = useCallback(
    async (member: OrganizationMember | WorkspaceInvite) => {
      const isConfirmed = await confirm({
        message: "Are you sure you want to leave this organization? This action cannot be undone",
        headerVariant: ConfirmationModalVariant.destructive,
        saveButtonVariant: "destructiveOutline",
        exitButtonVariant: "outline",
      });
      if (isConfirmed) {
        deleteOrganizationMember.mutate(member.id);
      }
    },
    [confirm, deleteOrganizationMember],
  );

  const userOwnerRoles = useMemo(
    () =>
      organizationRoles?.filter(
        (role: { name: string }) =>
          role.name.toLowerCase() === "user" || role.name.toLowerCase() === "owner",
      ) ?? [],
    [organizationRoles],
  );

  const roleActions: Action<NormalizedMemberData>[] = useMemo(
    () =>
      userOwnerRoles.map(role => ({
        id: `change-role-${role.id}`,
        label: `Change to ${role.name}`,
        onClick: (member?: NormalizedMemberData) => {
          if (member) {
            handleRoleChange(member, role.id.toString());
          }
        },
        getDisabled: (member?: NormalizedMemberData) => {
          if (!member || !isOrganizationMember(member)) {
            return true;
          }
          return member.organization_role.id === role.id || !isOrgOwner;
        },
      })),
    [userOwnerRoles, isOrgOwner, handleRoleChange],
  );

  const roleContextMenuItems: ContextMenuItems<NormalizedMemberData>[] = useMemo(
    () =>
      userOwnerRoles.map(role => ({
        id: `change-role-${role.id}`,
        label: `Change to ${role.name}`,
        onClick: (member: NormalizedMemberData) => {
          handleRoleChange(member, role.id.toString());
        },
        getDisabled: (member?: NormalizedMemberData) => {
          if (!member || !isOrganizationMember(member)) {
            return true;
          }
          return member.organization_role.id === role.id || !isOrgOwner;
        },
      })),
    [userOwnerRoles, isOrgOwner, handleRoleChange],
  );

  return {
    actions: roleActions,
    contextMenuItems: roleContextMenuItems,
    handlers: {
      handleRoleChange,
      handleDeleteMember,
      handleLeaveOrganization,
    },
  };
};
